import { useState } from 'react';
import { Button, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { peerLeaderSessionPrepApi } from '../../../services/api/peerLeaderSessionPrep';

interface MarkAsCompletedRsvpInterceptModalProps {
  opened: boolean;
  onClose: () => void;
  sessionId: string;
  sessionNumber: number | null;
  sessionDateLabel: string;
  cmName: string | null;
  /**
   * Called after a successful "Yes" or "Skip" branch — the page POSTs
   * mark-completed and refetches. The "No" branch handles the redirect
   * to /participant itself (declined leaders aren't allowed in prep).
   */
  onMarkedCompleted: () => void;
  onDeclined: () => void;
}

/**
 * AC13 — Intercept modal shown when the peer leader clicks "Mark as Completed"
 * but still has an unconfirmed RSVP. Three branches:
 *   - "Yes, I'll be there" → POST RSVP confirmed, then POST mark-completed
 *   - "No, please let {cm_name} know" → POST RSVP declined, then close + redirect
 *   - "Skip for now" → POST mark-completed without RSVP change
 *
 * Toast copy and merge tags mirror AC6 verbatim so the user experience is
 * consistent between the RSVP card (AC6) and this intercept (AC13).
 */
export function MarkAsCompletedRsvpInterceptModal({
  opened,
  onClose,
  sessionId,
  sessionNumber,
  sessionDateLabel,
  cmName,
  onMarkedCompleted,
  onDeclined,
}: MarkAsCompletedRsvpInterceptModalProps) {
  const [submitting, setSubmitting] = useState<null | 'confirmed' | 'declined' | 'skip'>(null);
  const sessionLabel = sessionNumber ? `Session ${sessionNumber}` : 'this session';
  const cmLabel = cmName ?? 'your Cohort Manager';
  const declineLabel = cmName
    ? `No, please let ${cmName} know`
    : "No, I can't lead today";

  const showError = (err: unknown) =>
    notifications.show({
      color: 'dangerRed',
      title: 'Something went wrong',
      message: err instanceof Error ? err.message : 'Please try again.',
    });

  const handleYes = async () => {
    setSubmitting('confirmed');
    try {
      await peerLeaderSessionPrepApi.setRsvp(sessionId, 'confirmed');
      await peerLeaderSessionPrepApi.markCompleted(sessionId);
      notifications.show({
        color: 'medleyGreen',
        title: 'Confirmed',
        message: sessionNumber
          ? `You're confirmed for Session ${sessionNumber}!`
          : "You're confirmed!",
      });
      onMarkedCompleted();
      onClose();
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(null);
    }
  };

  const handleNo = async () => {
    setSubmitting('declined');
    try {
      await peerLeaderSessionPrepApi.setRsvp(sessionId, 'declined');
      notifications.show({
        color: 'medleyGreen',
        title: 'Thanks',
        message: `Thanks for letting us know. ${cmLabel} has been notified.`,
      });
      onDeclined();
      onClose();
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(null);
    }
  };

  const handleSkip = async () => {
    setSubmitting('skip');
    try {
      await peerLeaderSessionPrepApi.markCompleted(sessionId);
      onMarkedCompleted();
      onClose();
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(null);
    }
  };

  const anyInFlight = submitting !== null;

  return (
    <Modal
      opened={opened}
      onClose={anyInFlight ? () => undefined : onClose}
      title="Before you finish — can you make it?"
      centered
      closeOnClickOutside={!anyInFlight}
      closeOnEscape={!anyInFlight}
    >
      <Stack gap="md">
        <Text size="sm">
          You haven&apos;t confirmed for {sessionLabel} on {sessionDateLabel} yet.
        </Text>
        <Stack gap="xs">
          <Button
            color="medleyGreen"
            fullWidth
            loading={submitting === 'confirmed'}
            disabled={anyInFlight && submitting !== 'confirmed'}
            onClick={() => void handleYes()}
          >
            Yes, I&apos;ll be there
          </Button>
          <Button
            variant="default"
            fullWidth
            loading={submitting === 'declined'}
            disabled={anyInFlight && submitting !== 'declined'}
            onClick={() => void handleNo()}
          >
            {declineLabel}
          </Button>
          <Button
            variant="subtle"
            color="gray"
            fullWidth
            loading={submitting === 'skip'}
            disabled={anyInFlight && submitting !== 'skip'}
            onClick={() => void handleSkip()}
          >
            Skip for now
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
